/**
 * The screening policy a compliance-facet person records (ADR-0071, SC2a).
 *
 * **No field has a default, anywhere.** The threshold, the maximum list age and the treatment of
 * weak aliases are counsel's choices (the maintainer's ruling of 2026-09-23), and a `.default()`
 * here would be engineering making them quietly. A body that omits one is refused; until a person
 * records a policy the screening provider fails closed.
 *
 * `WEAK_ALIAS_TREATMENTS` restates the kernel's vocabulary because this package depends on zod
 * alone; an API test binds the two copies so they cannot drift.
 */
import { z } from 'zod';
import { isCalendarDay } from './geography.js';
import { renderedText } from './rendered-text.js';
export const WEAK_ALIAS_TREATMENTS = ['screened', 'corroborating_only'];
/**
 * Why a list screening came back `review`, in the kernel's order (`REVIEW_REASON_ORDER`). Restated
 * here for the screens that translate them (SC2b); an API test binds the two copies.
 */
export const SCREENING_REVIEW_REASONS = ['score_at_or_above_threshold', 'script_not_covered'];
/**
 * Why a list screening could not run, in the kernel's readiness order
 * (`SCREENING_UNAVAILABLE_REASONS`). A refused onboarding or rescreen carries each as a detail
 * `{ path: 'screening', code }`, and the policy panel lists them (SC2c, ADR-0071 §8 as amended on
 * 2026-09-28); restated here for the screens that translate them, bound to the kernel's copy by an
 * API test, order included.
 */
export const SCREENING_UNAVAILABLE_REASONS = [
    'no_policy',
    'policy_review_lapsed',
    'test_source_in_production',
    'no_list',
    'fold_stale',
    'derivation_stale',
    'list_incomplete',
    'list_stale',
    'review_lapsed',
    'store_unavailable',
];
/**
 * The script of a screened name, by its letters (the kernel's `NameScript`): Latin, Arabic, both
 * mixed (`Zyyy`), or neither (`Zzzz`). match-1 compares only the first two, and a list covers only
 * those. The screens name them from this copy, which the API holds to the kernel's type at compile
 * time (`compliance/screening-vocabulary.ts`, SC2c).
 */
export const SCREENING_NAME_SCRIPTS = ['Latn', 'Arab', 'Zyyy', 'Zzzz'];
/**
 * The rest of what a list screening records and the compliance trail shows (SC2c), each restated
 * from the kernel's (or, for the entry type, the loader's) type for the screens that translate them,
 * and held to it at compile time in `compliance/screening-vocabulary.ts`: how completely the list
 * covers a name's script, which name was screened, how a name matched, and what kind of listed name
 * and party it matched.
 */
export const SCREENING_NAME_COVERAGES = ['complete', 'partial', 'none'];
export const SCREENED_NAME_FIELDS = ['legalName', 'legalNameLatin'];
export const SCREENING_MATCH_METHODS = ['symmetric', 'subject_in_list', 'list_in_subject'];
export const SCREENING_ALIAS_KINDS = ['primary', 'alias'];
export const SCREENING_ALIAS_QUALITIES = ['strong', 'weak'];
export const SCREENING_ENTRY_TYPES = ['individual', 'entity', 'vessel', 'aircraft', 'other'];
/**
 * What a person may rule about a listed party, and why an adjudication was recorded, in the kernel's
 * orders (`SCREENING_RULINGS`, `SCREENING_ADJUDICATION_REASONS`, SC3, ADR-0075). Restated for the
 * screens that translate them; an API test binds the copies, order included.
 */
export const SCREENING_RULINGS = ['not_a_match', 'match'];
export const SCREENING_ADJUDICATION_REASONS = ['hit_recorded', 'hit_lifted', 'ruled_not_a_match'];
/**
 * Why the compliance worksheet lists a listed party (SC3): it reached the threshold, a ruling set it
 * aside, or a ruling on it is in force although this screening did not score it.
 */
export const WORKSHEET_ENTRY_SOURCES = ['candidate', 'excluded', 'ruling'];
/**
 * Why the worksheet does not show a listed name (SC3): the list version holds none at that
 * reference, the response scan would refuse it as phone-shaped, or it holds a text-direction
 * override. The API decides from this list and the screens translate it.
 */
export const LISTED_NAME_WITHHELD = ['not_in_version', 'phone_shaped', 'direction_override'];
/**
 * Why a ruling on a list screening is refused (SC3, ADR-0075). A refusal carries each as a detail
 * `{ path, code }`, the screen translates it, and no value from the request is echoed:
 * - the screening ran against no list, a later one is on file, or the subject moved since;
 * - it names no listed party (a review that exists only because the script is not covered, whose
 *   remedy is the Latin name, SC2b);
 * - a party it does not name, one ruled twice, or a ruling already in force.
 */
export const SCREENING_ADJUDICATION_ISSUE_CODES = [
    'not_a_list_screening',
    'screening_superseded',
    'subject_changed',
    'nothing_to_rule',
    'not_a_candidate',
    'duplicate_entry',
    'ruling_unchanged',
];
/**
 * What followed a ruling once it was recorded (SC3): the Merchant was screened again; that screen
 * could not run; another write took the Merchant's stream first; or a person's match is in force,
 * so nothing was screened. The ruling stands in every case.
 */
export const SCREENING_ADJUDICATION_OUTCOMES = ['screened', 'unavailable', 'raced', 'hit_in_force'];
/**
 * A compliance person's ruling on the listed parties of one list screening (SC3, ADR-0075).
 *
 * Each party by reference, at the record digest it names, and nothing preselected: every ruling is
 * sent, none defaulted. The note is the person's reasoning, required, because the ruling is the
 * record a bank reads; the evidence reference is optional and names where the supporting document
 * is kept, never the document itself.
 */
export const adjudicateScreeningSchema = z
    .object({
    rulings: z
        .array(z
        .object({
        entryUid: z.string().regex(/^[A-Za-z0-9._-]{1,64}$/u),
        entryDigest: z.string().regex(/^[0-9a-f]{64}$/u),
        ruling: z.enum(SCREENING_RULINGS),
    })
        .strict())
        .min(1)
        .max(30),
    note: z.string().trim().min(1).max(2000),
    evidenceRef: z.string().trim().min(1).max(200).optional(),
})
    .strict();
export const adjudicateScreeningRendered = renderedText(adjudicateScreeningSchema, {
    prose: ['note'],
    refs: ['rulings.*.entryUid', 'evidenceRef'],
    exempt: {
        'rulings.*.entryDigest': 'sixty-four lowercase hex characters (the regex): one token, with no boundary inside it where a ' +
            'phone pattern could start or end',
    },
});
/**
 * The closed issue codes a refused policy carries; a screen translates them, values never echo. The
 * service's `refused()` takes only these, so a code it throws is one a screen has copy for.
 */
export const SCREENING_POLICY_ISSUE_CODES = [
    'list_source_refused',
    'list_not_loaded',
    'policy_unchanged',
    'phone_shaped',
    'direction_override',
];
/**
 * The screening providers `SCREENING_PROVIDER` may name (ADR-0071): the always-clear stub, or the
 * designated-party list. The API's environment schema reads this copy and the policy panel says
 * which one is running, so there is one list and no second to drift (SC2c).
 */
export const SCREENING_PROVIDER_KINDS = ['stub', 'designated_list'];
/**
 * The fields a policy is compared on, as the body names them. The note is not one: it says why a
 * change was made and is never a value a screening runs under. The service compares these, the
 * recorded event's `changed` lists them, and the panel labels them from this copy (SC2c).
 */
export const SCREENING_POLICY_FIELDS = [
    'listSource',
    'matchThreshold',
    'maxListAgeDays',
    'weakAliases',
    'owner',
    'reviewDate',
];
/**
 * Which way a recorded policy moved sensitivity, derived by the service from the change and never
 * taken from the request (ADR-0071 §4): any loosening ranks first. The service's type is this one,
 * and the panel translates the outcome from it (SC2c).
 */
export const SCREENING_POLICY_REASONS = [
    'policy_first',
    'sensitivity_lowered',
    'source_changed',
    'sensitivity_raised',
    'ownership_only',
];
export const recordScreeningPolicySchema = z
    .object({
    listSource: z.string().regex(/^[A-Z][A-Z0-9-]*$/u).max(64),
    /** The whole-percent score at or above which a screening is `review`. */
    matchThreshold: z.number().int().min(1).max(99),
    /** Days from OFAC's DateOfIssue beyond which a publication is too old to screen with. */
    maxListAgeDays: z.number().int().min(1).max(90),
    weakAliases: z.enum(WEAK_ALIAS_TREATMENTS),
    owner: z.string().trim().min(1).max(200),
    reviewDate: z.string().refine(isCalendarDay, 'review_date_format'),
    note: z.string().trim().min(1).max(500),
})
    .strict();
// Owner, note and list source are refused by the service, with its own codes (ADR-0071).
export const recordScreeningPolicyRendered = renderedText(recordScreeningPolicySchema, {
    exempt: {
        listSource: 'limited by LIST_SOURCE_SHAPE and checked by listSourceProblem, which refuses a phone-shaped ' +
            'source with its own code, list_source_refused (ADR-0071)',
        owner: 'checked by ScreeningPolicyService with its own code, phone_shaped (ADR-0071)',
        reviewDate: 'a calendar day (isCalendarDay, YYYY-MM-DD), which holds no phone-shaped digit run',
        note: 'checked by ScreeningPolicyService with its own code, phone_shaped (ADR-0071)',
    },
});
//# sourceMappingURL=screening.js.map